/**
 * Canonical recipient-routing contract for the Email Notifications admin UI
 * and every sender adapter.
 *
 * This module is intentionally dependency-free so the browser bundle and Deno
 * Edge Functions can consume the same declarations. `recipient_config`
 * contains only editable targets. Code-owned recipients declared in
 * `lockedTo` / `lockedCc` are assembled by the sender and can never be removed
 * by saving an empty or malicious config.
 */

export type NotificationRoutingMode =
  | 'authoritative'
  | 'required_plus_additions'
  | 'settings_primary'
  | 'display_only';

export type NotificationLoanScope = 'single' | 'multiple' | 'none';

export type NotificationPrimaryMode =
  | 'code_default_until_overridden'
  | 'locked_code_owned'
  | 'settings_owned';

export type RecipientTargetKind =
  | 'user'
  | 'user_role'
  | 'raw_email'
  | 'loan_role'
  | 'encompass_loan_role'
  | 'loan_party'
  | 'loan_contact';

export interface LockedRecipientDescriptor {
  label: string;
  /**
   * Stable implementation identifier. UI copy uses `label`; tests bind this
   * identifier to the owning sender rather than relying on prose.
   */
  source: string;
  /**
   * Fixed mailbox enforced by the shared envelope assembler. Dynamic locked
   * routes omit this value and remain the owning sender's responsibility.
   */
  fixedEmail?: string;
}

export interface NotificationRecipientContractEntry {
  routingMode: NotificationRoutingMode;
  loanScope: NotificationLoanScope;
  primaryMode: NotificationPrimaryMode;
  lockedTo: readonly LockedRecipientDescriptor[];
  lockedCc: readonly LockedRecipientDescriptor[];
  allowedToKinds: readonly RecipientTargetKind[];
  allowedCcKinds: readonly RecipientTargetKind[];
  senderAdapters: readonly string[];
  defaultSummary: string;
  compliance?: 'reg_b_appraisal_copy' | 'reg_x_servicing_transfer';
}

const STATIC_SETTINGS_KINDS = [
  'user',
  'raw_email'
] as const satisfies readonly RecipientTargetKind[];

const STATIC_ADDITIONAL_KINDS = [
  'user',
  'raw_email'
] as const satisfies readonly RecipientTargetKind[];

const SINGLE_LOAN_SETTINGS_KINDS = [
  'user',
  'raw_email',
  'loan_role',
  'encompass_loan_role'
] as const satisfies readonly RecipientTargetKind[];

const SINGLE_LOAN_ADDITIONAL_KINDS = [
  'user',
  'user_role',
  'raw_email',
  'loan_role',
  'encompass_loan_role'
] as const satisfies readonly RecipientTargetKind[];

const INTERNAL_SINGLE_LOAN_ADDITIONAL_KINDS = [
  'user',
  'user_role',
  'loan_role',
  'encompass_loan_role'
] as const satisfies readonly RecipientTargetKind[];

const AUTHORITATIVE_LOAN_KINDS = [
  'user',
  'user_role',
  'raw_email',
  'loan_role',
  'encompass_loan_role',
  'loan_party',
  'loan_contact'
] as const satisfies readonly RecipientTargetKind[];

const NONE: readonly RecipientTargetKind[] = [];

function route(
  label: string,
  source: string,
  fixedEmail?: string
): LockedRecipientDescriptor {
  return {
    label,
    source,
    ...(fixedEmail ? { fixedEmail } : {})
  };
}

function authoritative(
  senderAdapters: readonly string[],
  defaultSummary: string
): NotificationRecipientContractEntry {
  return {
    routingMode: 'authoritative',
    loanScope: 'single',
    primaryMode: 'code_default_until_overridden',
    lockedTo: [],
    lockedCc: [],
    allowedToKinds: AUTHORITATIVE_LOAN_KINDS,
    allowedCcKinds: AUTHORITATIVE_LOAN_KINDS,
    senderAdapters,
    defaultSummary
  };
}

function settingsPrimary(
  loanScope: NotificationLoanScope,
  senderAdapters: readonly string[],
  defaultSummary: string,
  options: {
    lockedTo?: readonly LockedRecipientDescriptor[];
    lockedCc?: readonly LockedRecipientDescriptor[];
  } = {}
): NotificationRecipientContractEntry {
  const kinds = loanScope === 'single'
    ? SINGLE_LOAN_SETTINGS_KINDS
    : STATIC_SETTINGS_KINDS;
  return {
    routingMode: 'settings_primary',
    loanScope,
    primaryMode: 'settings_owned',
    lockedTo: options.lockedTo ?? [],
    lockedCc: options.lockedCc ?? [],
    allowedToKinds: kinds,
    allowedCcKinds: kinds,
    senderAdapters,
    defaultSummary
  };
}

function requiredLoan(
  senderAdapters: readonly string[],
  lockedTo: readonly LockedRecipientDescriptor[],
  lockedCc: readonly LockedRecipientDescriptor[],
  defaultSummary: string,
  options: {
    allowedToKinds?: readonly RecipientTargetKind[];
    allowedCcKinds?: readonly RecipientTargetKind[];
    compliance?: NotificationRecipientContractEntry['compliance'];
  } = {}
): NotificationRecipientContractEntry {
  return {
    routingMode: 'required_plus_additions',
    loanScope: 'single',
    primaryMode: 'locked_code_owned',
    lockedTo,
    lockedCc,
    allowedToKinds: options.allowedToKinds ?? SINGLE_LOAN_ADDITIONAL_KINDS,
    allowedCcKinds: options.allowedCcKinds ?? SINGLE_LOAN_ADDITIONAL_KINDS,
    senderAdapters,
    defaultSummary,
    ...(options.compliance ? { compliance: options.compliance } : {})
  };
}

function requiredNonLoan(
  senderAdapters: readonly string[],
  lockedTo: readonly LockedRecipientDescriptor[],
  lockedCc: readonly LockedRecipientDescriptor[],
  defaultSummary: string,
  options: {
    allowedCcKinds?: readonly RecipientTargetKind[];
  } = {}
): NotificationRecipientContractEntry {
  return {
    routingMode: 'required_plus_additions',
    loanScope: 'none',
    primaryMode: 'locked_code_owned',
    lockedTo,
    lockedCc,
    allowedToKinds: NONE,
    allowedCcKinds: options.allowedCcKinds ?? STATIC_ADDITIONAL_KINDS,
    senderAdapters,
    defaultSummary
  };
}

function displayOnly(
  loanScope: NotificationLoanScope,
  senderAdapters: readonly string[],
  lockedTo: readonly LockedRecipientDescriptor[],
  lockedCc: readonly LockedRecipientDescriptor[],
  defaultSummary: string
): NotificationRecipientContractEntry {
  return {
    routingMode: 'display_only',
    loanScope,
    primaryMode: 'locked_code_owned',
    lockedTo,
    lockedCc,
    allowedToKinds: NONE,
    allowedCcKinds: NONE,
    senderAdapters,
    defaultSummary
  };
}

/**
 * Exactly the managed notification_settings rows expected after the latest
 * migrations. The drift suite pins the current count and full key set.
 * `payment_recorded` is a retired template alias and is intentionally absent.
 */
export const NOTIFICATION_RECIPIENT_CONTRACT = {
  consumer_sign_in_verification: displayOnly('none', ['consumer-api', '_shared/consumer-system-email-test'],
    [route('Email entered in the website verification step', 'consumer.verification_email')], [], 'Website visitor only; no CC or BCC'),
  consumer_quote_snapshot: displayOnly('none', ['consumer-api', '_shared/consumer-system-email-test'],
    [route('Verified owner of the saved quote', 'consumer.quote_owner')], [], 'Verified quote owner only; no CC or BCC'),

  admin_compensation_request: requiredNonLoan(
    ['send-notification'],
    [route('Compensation administrators', 'compensation.active_admins')],
    [],
    'Compensation administrators'
  ),
  admin_new_user_pending: requiredNonLoan(
    ['send-notification'],
    [route('All active administrators', 'users.active_admins')],
    [],
    'All active administrators'
  ),
  amended_return_detected: settingsPrimary(
    'single',
    ['send-loan-notification'],
    'Configured recipients for the triggering loan'
  ),
  appraisal_cda_order_required: requiredLoan(
    ['process-appraisal-cda-notifications'],
    [route('Assigned processor', 'loan.designated_processor')],
    [],
    'Assigned processor plus configured additions'
  ),
  appraisal_subject_to_required: settingsPrimary(
    'single',
    ['process-appraisal-workspace'],
    'Processor, Loan Officer, Loan Officer 2, Loan Officer Assistant and disclosures@lendwisemtg.com'
  ),
  appraisal_audit_review: settingsPrimary(
    'single',
    ['process-appraisal-workspace', 'send-notification'],
    'Assigned processor and Anthony'
  ),
  appraisal_audit_failure: settingsPrimary(
    'single',
    ['process-appraisal-workspace', 'send-notification'],
    'Anthony'
  ),
  appraisal_payment_link_reminder: displayOnly(
    'single', ['appraisal-workspace'],
    [route('Borrowers on the loan', 'appraisal.payment_reminder_borrowers')],
    [route('Loan officer, when someone else sends it', 'appraisal.payment_reminder_loan_officer')],
    'Borrowers, the loan officer when someone else sends it, and the sender (Bcc and Reply-To); no additional recipients'
  ),
  invoice_fee_daily_digest: settingsPrimary(
    'multiple',
    ['process-invoice-fee-digest', 'send-notification'],
    'Denise'
  ),
  invoice_fee_overage: settingsPrimary(
    'single',
    ['process-loan-fee-verifications', 'send-notification'],
    'Denise'
  ),
  invoice_fee_review: settingsPrimary(
    'single',
    ['process-loan-fee-verifications', 'send-notification'],
    'Denise'
  ),
  marketing_lo_stall_digest: requiredNonLoan(
    ['process-marketing-lo-stall-alerts'],
    [route('Loan officer who owns the stalled concepts', 'marketing.digest_owner')],
    [],
    'Enrolled loan officer plus configured CC (Brandon)'
  ),
  consumer_quote_failure: requiredNonLoan(
    ['consumer-quote-worker'],
    [route('Anthony — anthony@lendwisemtg.com', 'consumer.quote_failure_recipient', 'anthony@lendwisemtg.com')],
    [], 'Anthony plus configured CC'
  ),
  consumer_ad_measurement_health: displayOnly(
    'none', ['process-consumer-ad-measurement-health'],
    [route('Anthony — anthony@lendwisemtg.com', 'consumer.measurement_health_recipient', 'anthony@lendwisemtg.com')],
    [], 'Anthony only; no additional recipients'
  ),
  consumer_board_health: displayOnly(
    'none', ['process-consumer-board-health'],
    [route('Anthony — anthony@lendwisemtg.com', 'consumer.board_health_recipient', 'anthony@lendwisemtg.com')],
    [], 'Anthony only; no additional recipients'
  ),
  marketing_market_brief_completed: displayOnly(
    'none', ['process-marketing-market-brief-emails'],
    [route('Brandon — brandon@lendwisemtg.com', 'marketing.brief_recipient', 'brandon@lendwisemtg.com')],
    [], 'Brandon only; no additional recipients'
  ),
  // Marketing Requests (PR #38 completion): recipients are the row's identities, re-checked in SQL before every send.
  marketing_request_lo_update: displayOnly(
    'none', ['process-marketing-request-emails'],
    [route('Loan officer who owns the script request', 'marketing.request_lo')],
    [], 'The assigned loan officer only; no additional recipients'
  ),
  marketing_request_lo_reminder: displayOnly(
    'none', ['process-marketing-request-emails'],
    [route('Loan officer who owns the script request', 'marketing.request_lo')],
    [], 'The assigned loan officer only; no additional recipients'
  ),
  marketing_request_director_alert: displayOnly(
    'none', ['process-marketing-request-emails'],
    [route('Marketing director (host of the marketing calendar)', 'marketing.request_host')],
    [], 'The Marketing director only; no additional recipients'
  ),
  marketing_request_director_digest: displayOnly(
    'none', ['process-marketing-request-emails'],
    [route('Marketing director (host of the marketing calendar)', 'marketing.request_host')],
    [], 'The Marketing director only; no additional recipients'
  ),
  calendar_booking_update: displayOnly(
    'none', ['process-marketing-request-emails'],
    [route('Employee who booked the session', 'marketing.booking_booker')],
    [], 'The person who booked only; no additional recipients'
  ),
  artemis_operational_health: displayOnly(
    'none',
    ['process-artemis-health'],
    [route('Anthony — anthony@lendwisemtg.com', 'artemis.health_recipient', 'anthony@lendwisemtg.com')],
    [],
    'Anthony only; no additional recipients'
  ),
  artemis_daily_hunt_digest: requiredNonLoan(
    ['process-artemis-daily-digest', 'send-notification'],
    [route('Loan officer who owns this digest', 'artemis.digest_owner')],
    [],
    'Digest owner plus configured CC'
  ),
  artemis_in_range_alert: requiredNonLoan(
    ['artemis-price', 'send-notification'],
    [route('Loan officer who ran or owns the pricing scenario', 'artemis.pricing_owner')],
    [],
    'Pricing owner plus configured CC'
  ),
  avm_delivery_notification: requiredLoan(
    ['process-avm-notifications'],
    [route('Borrower and co-borrower with deliverable email', 'loan.borrower_pair')],
    [route('Required loan-team copy recipients', 'loan.avm_required_team')],
    'Borrower delivery plus configured additions',
    { compliance: 'reg_b_appraisal_copy' }
  ),
  cda_delivery_notification: requiredLoan(
    ['process-cda-notifications', 'send-cda-delivery-notification'],
    [route('Borrower and co-borrower with deliverable email', 'loan.borrower_pair')],
    [route('Required loan-team copy recipients', 'loan.cda_required_team')],
    'Borrower delivery plus configured additions',
    { compliance: 'reg_b_appraisal_copy' }
  ),
  clarifii_reliance_letter: requiredLoan(
    ['send-clarifii-reliance'],
    [
      route('Clarifii reliance mailbox', 'clarifii.vendor_recipient'),
      route('Investor-configured reliance contacts', 'investor.reliance_contacts')
    ],
    [route('Required internal reliance contact', 'clarifii.required_internal_cc')],
    'Vendor and investor contacts plus configured additions'
  ),
  clean_approval_ready: displayOnly(
    'single',
    ['clean-approval', 'encompass-webhook'],
    [
      route('Active assigned loan officer', 'loan.designated_loan_officer'),
      route('Active assigned processor', 'loan.designated_processor')
    ],
    [],
    'Fixed assigned loan officer and processor'
  ),
  closing_disclosure_sent: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  closing_documents_received: settingsPrimary(
    'single',
    ['process-closing-document-notifications', 'send-loan-notification'],
    'Configured funded-team recipients for the triggering loan'
  ),
  compensation_approved: requiredNonLoan(
    ['send-notification'],
    [route('Loan officer whose plan was approved', 'compensation.request_owner')],
    [],
    'Requesting loan officer plus configured CC'
  ),
  compensation_rejected: requiredNonLoan(
    ['send-notification'],
    [route('Loan officer whose plan was rejected', 'compensation.request_owner')],
    [],
    'Requesting loan officer plus configured CC'
  ),
  condition_reassignment: requiredLoan(
    ['_shared/finalize-reassignment'],
    [route('New condition assignee', 'condition.new_assignee')],
    [
      route('Assigned processor', 'loan.designated_processor'),
      route('Assigned loan officer', 'loan.designated_loan_officer')
    ],
    'New assignee and required loan team plus configured additions'
  ),
  conditions_portal_submission: requiredLoan(
    ['conditions-portal'],
    [route('Assigned processor', 'loan.designated_processor')],
    [route('Assigned loan officer', 'loan.designated_loan_officer')],
    'Assigned processor and loan officer plus configured additions'
  ),
  consumer_anonymous_ob_quota_alert: settingsPrimary(
    'none',
    ['consumer-quote-worker', 'send-notification'],
    'Configured anonymous consumer pricing-quota alert recipients'
  ),
  consumer_fee_schedule_drift_alert: settingsPrimary(
    'none',
    ['consumer-quote-worker', 'send-notification'],
    'Configured consumer fee-schedule drift alert recipients'
  ),
  consumer_rate_watch_lead_received: requiredNonLoan(
    ['consumer-rate-watch-worker', 'send-notification'],
    [route('Assigned eligible loan officer, or Anthony for assignment/review', 'consumer.rate_watch_lead_owner')],
    [],
    'Assigned eligible loan officer; otherwise Anthony. Optional active internal CC.',
    { allowedCcKinds: ['user'] }
  ),
  consumer_online_lead_unassigned: settingsPrimary(
    'none',
    [
      '_shared/consumer-online-lead-notifications',
      'consumer-api',
      'send-notification'
    ],
    'Configured administrators for unassigned Consumer Funnel Online Leads'
  ),
  consumer_online_lead_review_required: settingsPrimary(
    'none',
    ['_shared/consumer-online-lead-notifications', 'consumer-api', 'send-notification'],
    'Configured administrators for Consumer Funnel identity or ownership review'
  ),
  consumer_online_lead_assigned: requiredNonLoan(
    [
      '_shared/consumer-online-lead-notifications',
      'artemis-records',
      'consumer-api',
      'send-notification'
    ],
    [route('Assigned loan officer', 'artemis.online_lead_assignee')],
    [],
    'Assigned loan officer plus configured internal additions'
  ),
  disclosure_form_sent_to_lo: requiredLoan(
    ['process-disclosure-form-emails'],
    [route('Assigned loan officer', 'loan.designated_loan_officer')],
    [],
    'Assigned loan officer plus configured internal additions',
    {
      allowedToKinds: INTERNAL_SINGLE_LOAN_ADDITIONAL_KINDS,
      allowedCcKinds: INTERNAL_SINGLE_LOAN_ADDITIONAL_KINDS
    }
  ),
  disclosure_unsigned_reminder: requiredLoan(
    ['_shared/disclosure-reminder-sender', 'process-disclosure-reminders'],
    [route('Borrower with unsigned disclosures', 'disclosure.unsigned_borrower')],
    [route('Required assigned loan-team copy recipients', 'loan.disclosure_required_team')],
    'Unsigned borrower plus required loan team and configured additions'
  ),
  doc_order_changes_requested: authoritative(
    ['doc-order'],
    'Closing desk and assigned closer until explicitly customized'
  ),
  doc_order_lc_escalation: authoritative(
    ['doc-order'],
    'Lock desk until explicitly customized'
  ),
  doc_order_lo_signed: authoritative(
    ['doc-order'],
    'Assigned loan officer, processor, closer, and closing desk until explicitly customized'
  ),
  doc_order_sent_to_lo: authoritative(
    ['doc-order'],
    'Assigned loan officer and loan team until explicitly customized'
  ),
  doc_order_submitted: authoritative(
    ['doc-order'],
    'Closing desk, assigned closer, and loan team until explicitly customized'
  ),
  docs_out: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, LO Assistant, and Closer'
  ),
  file_submitted_processing: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  file_submitted_underwriting: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  file_suspended_underwriting: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Processor, Jr Processor, Loan Officer, and LO Assistant'
  ),
  final_approval_issued: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, LO Assistant, and Closer'
  ),
  flood_zone_identified: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  funded_not_ready_to_ship_reminder: settingsPrimary(
    'multiple',
    ['process-funded-ship-reminders'],
    'Configured recipients for a multi-loan digest'
  ),
  gbp_reviews_sync_alert: settingsPrimary(
    'none',
    ['_shared/gbp-reviews-sync', 'gbp-sync-reviews', 'send-notification'],
    'Configured operational-alert recipients'
  ),
  google_review_received: displayOnly(
    'single',
    ['_shared/gbp-review-congrats', '_shared/gbp-reviews-sync', 'gbp-sync-reviews'],
    [route('Assigned loan officer for the matched review', 'review.assigned_loan_officer')],
    [],
    'Assigned loan officer'
  ),
  lo_volume_bonus_agreement: displayOnly(
    'none',
    ['process-lo-volume-bonus-emails'],
    [route('Accepting loan officer (verified account email)', 'compensation.volume_bonus_recipient')],
    [],
    'Accepting loan officer only; immutable agreement, no recipient overrides'
  ),
  google_review_request: requiredLoan(
    ['_shared/review-request-sender', 'send-review-request'],
    [route('Each borrower with a deliverable email', 'loan.borrowers_individual_delivery')],
    [],
    'Borrower delivery plus configured copy recipients',
    { allowedToKinds: NONE }
  ),
  google_review_request_realtor: displayOnly(
    'single',
    ['_shared/review-request-sender', 'send-review-request'],
    [route('Each eligible purchase-loan realtor', 'loan.realtors_individual_delivery')],
    [],
    'Realtor recipients inherited from Google Review Request'
  ),
  initial_disclosures_sent: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  intent_to_proceed_outstanding: authoritative(
    ['process-disclosure-due-reminders', 'process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  intent_to_proceed_received: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  le_due_disclosures_required: authoritative(
    ['process-disclosure-due-reminders', 'process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  loan_funded: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  loan_purchased: settingsPrimary(
    'single',
    ['process-loan-lifecycle-notifications', 'send-loan-notification'],
    'Configured purchased-loan recipients'
  ),
  lock_expiration_secondary_digest: settingsPrimary(
    'multiple',
    ['process-lock-expiration-secondary-digest'],
    'Configured recipients for a multi-loan digest'
  ),
  lock_expiration_warning: requiredLoan(
    ['process-lock-expiration-reminders'],
    [route('Assigned loan officer', 'loan.designated_loan_officer')],
    [route('Assigned processor', 'loan.designated_processor')],
    'Assigned loan officer and processor plus configured additions'
  ),
  lo_compensation_breakdown: requiredLoan(
    ['send-notification', 'BankerRecon'],
    [route('Loan officer receiving the breakdown', 'compensation.loan_officer')],
    [],
    'Compensation recipient plus configured additions'
  ),
  lo_prospect_preview_invite: requiredNonLoan(
    ['lo-prospect-invite', 'send-notification'],
    [route('Invited loan officer', 'talent.invited_prospect')],
    [
      route('Invite sender', 'talent.invite_sender'),
      route('Assigned recruiter', 'talent.assigned_recruiter'),
      route(
        'Talent intake mailbox',
        'talent.intake_mailbox',
        'join@lendwisemtg.com'
      )
    ],
    'Invited prospect plus required Talent copies and configured CC'
  ),
  lo_prospect_registration_confirm: requiredNonLoan(
    ['lo-prospect-register', 'send-notification'],
    [route('Registering loan officer', 'talent.registering_prospect')],
    [route(
      'Talent intake mailbox',
      'talent.intake_mailbox',
      'join@lendwisemtg.com'
    )],
    'Registering prospect plus required Talent copy and configured CC'
  ),
  lo_prospect_registration_received: settingsPrimary(
    'none',
    ['lo-prospect-register', 'send-notification'],
    'Configured Talent intake recipients'
  ),
  lo_prospect_scenario_failure: requiredNonLoan(
    ['lo-prospect-price-public', 'send-notification'],
    [route('Assigned recruiter', 'talent.assigned_recruiter')],
    [route(
      'Talent intake mailbox',
      'talent.intake_mailbox',
      'join@lendwisemtg.com'
    )],
    'Assigned recruiter plus required Talent copy and configured CC',
    { allowedCcKinds: ['user'] }
  ),
  lo_prospect_scenario_run: requiredNonLoan(
    ['lo-prospect-price-public', 'send-notification'],
    [
      route('Invite sender', 'talent.invite_sender'),
      route('Assigned recruiter', 'talent.assigned_recruiter')
    ],
    [route(
      'Talent intake mailbox',
      'talent.intake_mailbox',
      'join@lendwisemtg.com'
    )],
    'Talent owners plus required intake copy and configured CC',
    { allowedCcKinds: ['user'] }
  ),
  mi_activation_required: settingsPrimary(
    'single',
    ['process-loan-lifecycle-notifications', 'send-loan-notification'],
    'Configured MI activation recipients'
  ),
  milestone_approval_ready: requiredLoan(
    ['encompass-webhook'],
    [
      route('Assigned loan officer', 'loan.designated_loan_officer'),
      route('Assigned processor', 'loan.designated_processor')
    ],
    [
      route('Assigned loan officer 2, when present', 'loan.designated_loan_officer_2'),
      route('Assigned loan officer assistant, when present', 'loan.designated_loan_officer_assistant')
    ],
    'Assigned loan officer and processor, required loan-team copies, plus configured additions'
  ),
  milestone_approval_ready_nondel: requiredLoan(
    ['encompass-webhook'],
    [
      route('Assigned loan officer', 'loan.designated_loan_officer'),
      route('Assigned processor', 'loan.designated_processor')
    ],
    [
      route('Assigned loan officer 2, when present', 'loan.designated_loan_officer_2'),
      route('Assigned loan officer assistant, when present', 'loan.designated_loan_officer_assistant')
    ],
    'Assigned loan officer and processor, required loan-team copies, plus configured additions'
  ),
  nmls_authorization_granted: requiredNonLoan(
    ['send-notification'],
    [
      route('Invite sender', 'talent.invite_sender'),
      route('Assigned recruiter', 'talent.assigned_recruiter')
    ],
    [route(
      'Talent intake mailbox',
      'talent.intake_mailbox',
      'join@lendwisemtg.com'
    )],
    'Talent owners plus required intake copy and configured CC'
  ),
  override_compensation_breakdown: requiredLoan(
    ['send-notification', 'BankerRecon'],
    [route('Configured override recipient for the loan officer', 'compensation.override_recipient')],
    [],
    'Override recipient plus configured additions'
  ),
  payout_payment_confirmation: requiredNonLoan(
    ['send-notification'],
    [route('Loan officer receiving the payment confirmation', 'payout.loan_officer')],
    [],
    'Payment recipient plus configured internal CC',
    { allowedCcKinds: ['user'] }
  ),
  lo_profitability_bonus_awarded: requiredNonLoan(
    ['send-notification'],
    [route('Loan officer receiving the saved award', 'compensation.bonus_recipient')],
    [],
    'Award recipient plus configured CCs; Anthony chooses whether to send after saving'
  ),
  pickup_reprice_opportunity: settingsPrimary(
    'multiple',
    ['ob-pickup-analysis', 'send-notification'],
    'Configured Secondary desk recipients'
  ),
  preapproval_issued: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  preapproval_requested: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  preapproval_submitted_underwriting: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  price_finalized: settingsPrimary(
    'single',
    ['process-loan-lifecycle-notifications'],
    'Configured funding recipients with the saving user copied',
    {
      lockedCc: [
        route('User who finalized pricing', 'pricing.finalized_by')
      ]
    }
  ),
  pricing_field_changed: settingsPrimary(
    'single',
    ['process-loan-lifecycle-notifications'],
    'Configured Product and Pricing recipients'
  ),
  profile_lead_consumer_confirm: displayOnly(
    'none',
    ['_shared/profile-lead-notifications'],
    [route('Consumer who submitted the profile form', 'profile.submitting_consumer')],
    [],
    'Submitting consumer'
  ),
  profile_lead_lo_notification: requiredNonLoan(
    ['_shared/profile-lead-notifications', 'send-notification'],
    [route('Loan officer who owns the public profile', 'profile.assigned_loan_officer')],
    [],
    'Profile owner plus configured CC'
  ),
  rate_lock_confirmation: requiredLoan(
    ['rate-lock-confirmation'],
    [route('Assigned loan officer', 'loan.designated_loan_officer')],
    [route('Required assigned loan-team copy recipients', 'loan.rate_lock_required_team')],
    'Assigned loan officer and required loan team plus configured additions'
  ),
  // Same routes as the confirmation it replaces; its own settings row holds the additions.
  rate_lock_confirmation_critical: requiredLoan(
    ['rate-lock-confirmation'],
    [route('Assigned loan officer', 'loan.designated_loan_officer')],
    [route('Required assigned loan-team copy recipients', 'loan.rate_lock_required_team')],
    'Assigned loan officer and required loan team plus configured additions'
  ),
  rate_lock_expired: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  recruit_sign_in_verification: requiredNonLoan(
    ['recruit-auth', 'process-recruit-auth', 'send-notification'],
    [route('Saved recruit email', 'talent.recruit_personal_email')],
    [route('Assigned recruiter (receives the working code)', 'talent.assigned_recruiter')],
    'Saved recruit email and assigned recruiter, plus active internal staff CC',
    { allowedCcKinds: ['user'] }
  ),
  recruit_tech_setup_email: requiredNonLoan(
    ['send-recruit-onboarding-email', 'send-notification'],
    [route('Recruit completing onboarding', 'talent.recruit')],
    [
      route('Sending administrator', 'talent.message_sender'),
      route('Assigned recruiter', 'talent.assigned_recruiter'),
      route(
        'Talent intake mailbox',
        'talent.intake_mailbox',
        'join@lendwisemtg.com'
      )
    ],
    'Recruit plus required Talent copies and configured CC'
  ),
  recruit_partial_onboarding_email: displayOnly(
    'none',
    ['send-recruit-onboarding-email', 'send-notification'],
    [route('Recruit personal email', 'talent.recruit_personal_email')],
    [],
    'Recruit personal email only; credential-bearing and never copied'
  ),
  recruit_nmls_clearance: requiredNonLoan(
    ['send-recruit-onboarding-email', 'send-notification'],
    [route('Recruit company Gmail', 'talent.recruit_company_gmail')],
    [],
    'Recruit company Gmail plus configured CC'
  ),
  recruiter_company_signed: requiredNonLoan(
    ['send-notification'],
    [
      route('Invite sender', 'talent.invite_sender'),
      route('Assigned recruiter', 'talent.assigned_recruiter')
    ],
    [route(
      'Talent intake mailbox',
      'talent.intake_mailbox',
      'join@lendwisemtg.com'
    )],
    'Talent owners plus required intake copy and configured CC'
  ),
  recruiter_invitation_sent: requiredNonLoan(
    ['send-notification'],
    [
      route('Invite sender', 'talent.invite_sender'),
      route('Assigned recruiter', 'talent.assigned_recruiter')
    ],
    [route(
      'Talent intake mailbox',
      'talent.intake_mailbox',
      'join@lendwisemtg.com'
    )],
    'Talent owners plus required intake copy and configured CC'
  ),
  recruiter_lo_signed: requiredNonLoan(
    ['send-notification'],
    [
      route('Invite sender', 'talent.invite_sender'),
      route('Assigned recruiter', 'talent.assigned_recruiter')
    ],
    [route(
      'Talent intake mailbox',
      'talent.intake_mailbox',
      'join@lendwisemtg.com'
    )],
    'Talent owners plus required intake copy and configured CC'
  ),
  recruiter_onboarding_complete: requiredNonLoan(
    ['send-notification'],
    [
      route('Invite sender', 'talent.invite_sender'),
      route('Assigned recruiter', 'talent.assigned_recruiter')
    ],
    [route(
      'Talent intake mailbox',
      'talent.intake_mailbox',
      'join@lendwisemtg.com'
    )],
    'Talent owners plus required intake copy and configured CC'
  ),
  recruiting_intro_call_booked: requiredNonLoan(
    ['recruiting-bookings', 'send-notification'],
    [route('Host of the booked call', 'recruiting.booking_host')],
    [],
    'The host of the booked call plus configured CC'
  ),
  redisclosure_required: authoritative(
    ['process-loan-lifecycle-notifications'],
    'Loan Officer with LO2, Processor, and LO Assistant'
  ),
  servicing_goodbye_letter_failed: settingsPrimary(
    'none',
    ['_shared/goodbye-letter-sender', 'send-goodbye-letter'],
    'Configured additional recipients'
  ),
  servicing_goodbye_letter: requiredLoan(
    ['_shared/goodbye-letter-sender', 'send-goodbye-letter'],
    [route('Borrower and co-borrower with deliverable email', 'servicing.borrower_pair')],
    [
      route('Assigned loan officer and LO2', 'loan.servicing_required_team'),
      route(
        'Servicing mailbox',
        'servicing.required_mailbox',
        'servicing@lendwisemtg.com'
      )
    ],
    'Borrowers and required servicing copies plus configured additions',
    { compliance: 'reg_x_servicing_transfer' }
  ),
  servicing_payment_received: requiredLoan(
    ['send-servicing-payment-received'],
    [route('Borrower on the servicing record', 'servicing.borrower')],
    [
      route('Assigned loan officer and LO2', 'loan.servicing_required_team'),
      route(
        'Servicing mailbox',
        'servicing.required_mailbox',
        'servicing@lendwisemtg.com'
      )
    ],
    'Borrower and required servicing copies plus configured additions'
  ),
  servicing_payment_reminder: requiredLoan(
    ['send-servicing-payment-reminder'],
    [route('Borrower on the servicing record', 'servicing.borrower')],
    [
      route('Assigned loan officer and LO2', 'loan.servicing_required_team'),
      route(
        'Servicing mailbox',
        'servicing.required_mailbox',
        'servicing@lendwisemtg.com'
      )
    ],
    'Borrower and required servicing copies plus configured additions'
  ),
  title_escrow_portal_invitation: requiredLoan(
    ['conditions-portal'],
    [route('Assigned escrow officer', 'loan.escrow_officer')],
    [
      route('Assigned processor', 'loan.designated_processor'),
      route('Assigned loan officer', 'loan.designated_loan_officer')
    ],
    'Escrow officer and required loan team plus configured additions',
    { allowedToKinds: NONE }
  ),
  transferred_appraisal_delivery: requiredLoan(
    ['process-transferred-appraisal-notifications'],
    [route('Borrower and co-borrower with deliverable email', 'loan.borrower_pair')],
    [route('Required loan-team copy recipients', 'loan.transferred_appraisal_required_team')],
    'Borrower delivery plus configured additions',
    { compliance: 'reg_b_appraisal_copy' }
  ),
  encompass_document_sync_failed: settingsPrimary(
    'none',
    ['encompass-document-sync', 'send-notification'],
    'Configured operational-alert recipients'
  ),
  appraisal_delivery_exception: settingsPrimary(
    'none',
    [
      'process-appraisal-cda-notifications',
      'process-cda-notifications',
      'process-transferred-appraisal-notifications',
      'process-avm-notifications'
    ],
    'Configured operational-alert recipients'
  ),
  twilio_webhook_drift: settingsPrimary(
    'none',
    ['twilio-check-config', 'send-notification'],
    'Configured operational-alert recipients'
  ),
  user_approved: requiredNonLoan(
    ['send-notification'],
    [route('Newly approved user', 'users.approved_user')],
    [],
    'Approved user plus configured CC'
  ),
  // The assigned processor remains a required, code-owned route. As with the
  // CDA-order notification, administrators may augment that route with
  // Additional TO and CC recipients but cannot remove or redirect it. The
  // sender still fails closed when Encompass role 5 is missing, ambiguous,
  // stale, inactive, or unresolved.
  warehouse_minimum_fico_exception: requiredLoan(
    ['process-loan-lifecycle-notifications'],
    [route('Assigned processor', 'loan.current_encompass_role_5')],
    [],
    'Assigned processor plus configured additions'
  )
} as const satisfies Record<string, NotificationRecipientContractEntry>;

export type ManagedNotificationType = keyof typeof NOTIFICATION_RECIPIENT_CONTRACT;

export const MANAGED_NOTIFICATION_TYPES = Object.freeze(
  Object.keys(NOTIFICATION_RECIPIENT_CONTRACT) as ManagedNotificationType[]
);

export function getNotificationRecipientContract(
  notificationType: string
): NotificationRecipientContractEntry | null {
  return Object.prototype.hasOwnProperty.call(
    NOTIFICATION_RECIPIENT_CONTRACT,
    notificationType
  )
    ? NOTIFICATION_RECIPIENT_CONTRACT[
        notificationType as ManagedNotificationType
      ]
    : null;
}

export function contractAllowsKind(
  notificationType: string,
  zone: 'to' | 'cc',
  kind: RecipientTargetKind
): boolean {
  const contract = getNotificationRecipientContract(notificationType);
  if (!contract) {
    return false;
  }
  const allowed = zone === 'to'
    ? contract.allowedToKinds
    : contract.allowedCcKinds;
  return allowed.includes(kind);
}
